import type { RoleId } from '@onuw/roles-data'
import type { RoleGuide } from './types'

export const ALIEN_GUIDES = {
  oracle: {
    lead: 'Answers a question from the app in front of the whole table, and may change sides by the answer.',
    does: [
      'The Oracle wakes first. The app asks one question, drawn for the game, and everyone hears it: would you like to join the other team, to view the center cards, to exchange your card with one from the center, or is your number odd or even? Everyone then hears what came of the answer.',
    ],
    rules: [
      'Joining the Werewolves turns her card into a Werewolf card. Joining the Aliens or the Vampires puts her on that team: she wins only if it does, and is not one of its cards.',
      'Asking for the center cards lets her view one, two or three of them, as the app decides.',
      'Asking to exchange usually swaps her card with a center card she does not see. One time in four the app refuses.',
      'The odd-or-even answer tells the table whether her player number is odd or even, which narrows down who she is.',
      'The question is asked even when the Oracle card lies in the center.',
    ],
    tips: [
      'The table heard the question and the result, so your night is half public. Build your claim on it.',
      'Saying yes to the other team is a real offer: you become their only member nobody among them knows.',
    ],
    against: [
      'An Oracle who said yes is an enemy the village cannot find by counting wolves. The announcement tells you she exists, not who she is.',
    ],
  },
  alien: {
    lead: 'The Aliens wake together, see each other, and do what the app tells them: a different action every game.',
    does: [
      'Everything alien wakes together: the Alien cards, [[groob]], [[zerb]], the [[syntheticAlien]] and the [[bodySnatcher]]. The app announces one action to the whole table and the Aliens carry it out. Each looks at a player or at a center card, they all look at one card together, each passes their card to the Alien on their left or right, they show each other their cards, or they do nothing.',
    ],
    rules: [
      'A deck holds up to two Alien cards.',
      'The Aliens win if no Alien dies, and lose if the [[tanner]] dies.',
      'Some actions choose by number: with these roles in the deck every player has a number, and "an even player" or "a higher player" means that number.',
      'Passing cards moves each Alien card to the next Alien. Everyone is still an Alien, but Groob, Zerb and the Synthetic Alien may have changed hands.',
      'The action is announced even when no Alien is among the players.',
    ],
    tips: [
      'The table heard your action. Tell a story that fits what the Aliens could have learned from it.',
      'If the Aliens all looked at one center card, you share a claim nobody can contradict. Do not all use it.',
    ],
    against: [
      'The announced action tells you what the Aliens know. "All Aliens, view a center card" means they hold one safe claim between them.',
    ],
  },
  syntheticAlien: {
    lead: 'Wakes with the Aliens but plays for itself: it wins only by dying.',
    does: [
      'The Synthetic Alien wakes with the Aliens, sees who they are and takes part in the alien action.',
    ],
    rules: [
      'It wins only if it dies. When it does, the village and the Aliens both lose.',
      'It is not an Alien for the village: killing it does not count as killing one.',
      'The [[cow]] and the [[leader]] count it among the Aliens.',
      'The Aliens cannot tell it from one of their own, unless the action shows them the cards.',
    ],
    tips: [
      'You know the Aliens, and they take you for one. Get caught in their company: the village will want to vote for the Alien it can name.',
    ],
    against: [
      'With the Synthetic Alien in the deck, an Alien who seems to be throwing the game may not be one. The real Aliens need it alive as much as the village does.',
    ],
  },
  groob: {
    lead: 'An Alien with a rival: when Zerb is at the table too, Groob wins only if Zerb dies and Groob survives.',
    does: [
      'Groob wakes with the Aliens and takes part in the alien action. Then Groob and [[zerb]] wake again, alone, and look for each other.',
    ],
    rules: [
      'With both among the players when the night ends, each wins only if the other dies and they survive themselves.',
      'The village still wins by killing either of them, and the other Aliens lose when one of them dies.',
      'If Zerb did not wake, Groob plays as a regular Alien.',
      'The [[leader]] sees the two point at each other, and then wins only if both survive.',
    ],
    tips: [
      'You need a death on your own team. Feed the village what it needs to vote Zerb out, without being caught as an Alien yourself.',
    ],
    against: [
      'An Alien who helps the village find another Alien is strange, unless it is Groob or Zerb. If one of them is named, the namer may be the other.',
    ],
  },
  zerb: {
    lead: 'Groob’s rival: when both are at the table, Zerb wins only if Groob dies and Zerb survives.',
    does: [
      'Zerb wakes with the Aliens and takes part in the alien action. Then Zerb and [[groob]] wake again, alone, and look for each other.',
    ],
    rules: [
      'With both among the players when the night ends, each wins only if the other dies and they survive themselves.',
      'A death of either one is still an Alien’s death: the village wins and the other Aliens lose.',
      'If Groob did not wake, Zerb plays as a regular Alien.',
      'Passing cards during the alien action can hand the Zerb card to another Alien: whoever holds it when the rivals wake is Zerb.',
    ],
    tips: [
      'The other Aliens want both of you alive. You cannot trust them to help, and they cannot trust you.',
    ],
    against: [
      'With Groob and Zerb both in the deck, the Aliens are divided from the first minute. Offer one of them the other’s name and see who takes it.',
    ],
  },
  leader: {
    lead: 'A villager who knows the Aliens. If every one of them votes for the Leader, the Leader dies and the village loses.',
    does: [
      'The Leader wakes and sees who the Aliens are. In a game without alien cards, the Leader sees the Werewolves instead.',
    ],
    rules: [
      'If all the Aliens vote for the Leader ([[groob]] and [[zerb]] included, the [[syntheticAlien]] not), the Leader dies whatever the count, the village loses and the Aliens win.',
      'Without alien cards in the deck the same goes for the Werewolves.',
      'With Groob and Zerb both at the table, the Leader sees them point at each other, and wins only if both survive.',
    ],
    tips: [
      'With one Alien at the table, that Alien’s single vote kills you. Share what you know as if you were another role, and never show the Aliens who you are.',
    ],
    against: [
      'For the Aliens, finding the Leader and voting together is the easiest win in the game. Listen for the player who knows too much.',
    ],
  },
  cow: {
    lead: 'Learns whether one of the players sitting next to it is an Alien.',
    does: [
      'After the Aliens wake, the Cow is told whether it felt a tap: at least one of its two neighbours is an Alien, or neither is. Without alien cards in the deck it senses Werewolves instead.',
    ],
    rules: [
      'It learns that a neighbour is one, not which neighbour.',
      'The [[syntheticAlien]], [[groob]], [[zerb]] and the [[bodySnatcher]] count as Aliens.',
      'The answer is about the cards as the Aliens woke. The Body Snatcher and the roles that move cards act later.',
    ],
    tips: [
      '"Neither neighbour" clears two players at once and is worth saying early. "A tap" gives the table two suspects.',
    ],
    against: [
      'An Alien sitting next to the Cow is half caught, and knows where the Cow could be. Claiming Cow first is the counter.',
    ],
  },
  psychic: {
    lead: 'Looks at cards as the app instructs: one player this game, two neighbours or a center card the next.',
    does: [
      'The app announces the Psychic’s instruction to the whole table: view any player, two players, one or both neighbours, an even or an odd numbered player, a player with a higher or lower number, one center card or two. The Psychic may then look accordingly.',
    ],
    rules: [
      'The instruction is announced even when the card lies in the center.',
      'A shielded card cannot be viewed.',
      'She wakes after the [[seer]] and before the [[robber]], so the cards she saw may still move.',
    ],
    tips: [
      'The table knows what kind of look you had. Say exactly whom or what you saw: a claim that fits the instruction is easy to believe.',
    ],
    against: [
      'A wolf who claims Psychic must name a target the announced instruction allowed. Check the numbers and the seats.',
    ],
  },
  exposer: {
    lead: 'Turns one, two or three center cards face up for everyone.',
    does: [
      'Near the end of the night the app announces how many center cards the Exposer flips. The Exposer may choose that many, and they stay face up for the whole table.',
    ],
    rules: [
      'Only center cards that are still face down can be chosen.',
      'She acts after every card has moved, so the exposed cards are what the center holds when the night ends.',
    ],
    tips: [
      'An exposed card is a role no player holds now. Whoever was dealt it has some explaining to do: the [[drunk]] or the [[witch]] may have put it there.',
    ],
    against: [
      'With the Exposer in the deck, claiming a role from the center is risky: it may be lying face up in the morning.',
    ],
  },
  blob: {
    lead: 'The Blob absorbs the players next to it and wins if all of them, the Blob included, survive.',
    does: [
      'The Blob wakes and is told which players are part of it. Their number depends on the table: one neighbour with four players, two with five or six, three with seven or eight, four with nine or more. With three players the Blob is alone.',
    ],
    rules: [
      'An even number splits across both sides. An odd number runs to one side, left or right, drawn for the game.',
      'The Blob wins if nobody in it dies, whichever team wins as well.',
      'The members are seats, not cards, and they are not told they belong to it.',
    ],
    tips: [
      'You need the vote to land outside your group, or nowhere. Whether your neighbours are wolves does not matter to you.',
    ],
    against: [
      'The Blob is not the village’s friend. It will defend a wolf who sits inside it as warmly as a villager.',
    ],
  },
  mortician: {
    lead: 'Wins if one of the players sitting next to them dies.',
    does: [
      'Near the end of the night the app announces the Mortician’s instruction: look at one neighbour’s card, at both, at the left one, at the right one, or at your own. The Mortician may look accordingly.',
    ],
    rules: [
      'The Mortician wins if either neighbour dies, whether or not the Mortician survives.',
      'He looks after every card has moved, so what he sees is final.',
      'He plays for himself, and wins or loses apart from every team.',
    ],
    tips: [
      'You need a neighbour voted out, and you have probably seen one of their cards. If it is a wolf, the truth is your best weapon. If not, you need a better lie.',
    ],
    against: [
      'A Mortician’s accusation of a neighbour is always in his own interest. Ask what the announced instruction let him see.',
    ],
  },
  rascal: {
    lead: 'Moves cards as the app instructs: like a Troublemaker this game, like a Robber, a Witch, a Village Idiot or a Drunk the next.',
    does: [
      'The app announces the Rascal’s instruction to the whole table. He may swap two other players’ cards (any two, his two neighbours, two even or odd numbered players, two with higher or lower numbers than his), rob a player and look at the card, look at a center card and give it to a player, shift every other card one seat, or trade his own card for a center card unseen.',
    ],
    rules: [
      'Every instruction is optional, except that a Rascal who looks at a center card must then swap it, as the [[witch]] must.',
      'He wakes after the [[troublemaker]] and the [[gremlin]], and before the [[drunk]].',
      'The instruction is announced even when the card lies in the center.',
    ],
    tips: [
      'The table knows what kind of move you could make. Say which players you chose, and who holds what now.',
    ],
    against: [
      'When the instruction limits the Rascal to neighbours or to numbers, only some players can have been moved. Work out which before believing a swap story.',
    ],
  },
} satisfies Partial<Record<RoleId, RoleGuide>>
